import { useEffect, useMemo, useState } from "react";
import { api, fmtIsoDateOnly } from "@/lib/api";
import { PageHeader, Section, Btn, Select } from "@/components/Shared";
import { Calendar, History, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

// Rate Calendar — Admin → Settings → Rate Calendar
//
// Visualises every rate version across all 3 scopes (merchant_mid /
// gateway_lob / partner_lob) and lets admins browse upcoming + historical
// rate-change events.

const SCOPE_LABEL = {
  merchant_mid: "Merchant",
  gateway_lob: "Gateway",
  partner_lob: "Partner",
};

const fmtRate = (v) => `${(Number(v || 0) * 100).toFixed(4).replace(/\.?0+$/, "")}%`;
const fmtDate = fmtIsoDateOnly;

// Render the entity column. For merchant_mid rows the rate-version itself
// only carries `merchant_id`, so we enrich via the merchants list (passed
// in) to display merchant_name + gateway + LOB — matching the level of
// detail already shown for partner_lob rows (iter31.3 user request).
function describeKey(r, merchantsById = {}) {
  if (r.scope === "merchant_mid") {
    const m = merchantsById[r.merchant_id];
    if (m) {
      const gw = m.gateway || "—";
      const lob = m.lob || "—";
      const name = m.merchant_name || r.merchant_id;
      return `${r.merchant_id} · ${name} · ${gw} · ${lob}`;
    }
    return r.merchant_id || "—";
  }
  if (r.scope === "gateway_lob") return `${r.gateway || "—"} · ${r.lob || "—"}`;
  if (r.scope === "partner_lob") return `${r.partner_id || "—"} · ${r.gateway || "—"} · ${r.lob || "—"}`;
  return "—";
}

// Group rate versions by month-of-effective_from. Rows already sorted by
// effective_from desc on the server; flip to asc and group here.
function groupByMonth(rows) {
  const sorted = [...rows].sort((a, b) => new Date(a.effective_from) - new Date(b.effective_from));
  const out = new Map();  // "2026-04" → [rows]
  for (const r of sorted) {
    const m = fmtDate(r.effective_from).slice(0, 7);
    if (!out.has(m)) out.set(m, []);
    out.get(m).push(r);
  }
  return out;
}

// For the timeline view we want to show old→new transitions per key. Walk
// each key's history in chronological order; old is the previous row's rate.
function pairWithPrior(rows) {
  const byKey = new Map();
  const sorted = [...rows].sort((a, b) => new Date(a.effective_from) - new Date(b.effective_from));
  for (const r of sorted) {
    const k = `${r.scope}|${r.merchant_id || ""}|${r.gateway || ""}|${r.lob || ""}|${r.partner_id || ""}`;
    if (!byKey.has(k)) byKey.set(k, []);
    byKey.get(k).push(r);
  }
  const enriched = [];
  for (const arr of byKey.values()) {
    arr.forEach((r, i) => {
      enriched.push({ ...r, _prior_rate: i > 0 ? arr[i - 1].rate_value : null });
    });
  }
  return enriched;
}

export default function RateCalendar() {
  const [view, setView] = useState("timeline");  // timeline | calendar
  const [rows, setRows] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scopeFilter, setScopeFilter] = useState("");
  const [futureOnly, setFutureOnly] = useState(false);
  const [cursor, setCursor] = useState(() => new Date().toISOString().slice(0, 7));  // YYYY-MM
  const [selectedDay, setSelectedDay] = useState(null);  // YYYY-MM-DD

  const load = async () => {
    setLoading(true);
    try {
      const params = scopeFilter ? { scope: scopeFilter } : {};
      const [rv, mr] = await Promise.all([
        api.get("/rate-versions", { params }),
        // Fetch merchants once to enrich merchant_mid rows with
        // gateway/LOB/name. Cheap (~30 rows) and lets the calendar
        // render `M001 · Acme · Cashfree · UPI` without a backend change.
        api.get("/merchants"),
      ]);
      setRows(Array.isArray(rv.data) ? rv.data : []);
      setMerchants(Array.isArray(mr.data) ? mr.data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load rate calendar");
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scopeFilter]);

  const merchantsById = useMemo(() => {
    const out = {};
    for (const m of merchants) out[m.merchant_id] = m;
    return out;
  }, [merchants]);

  const filtered = useMemo(() => {
    let list = rows;
    if (futureOnly) {
      const today = new Date().toISOString().slice(0, 10);
      list = list.filter((r) => fmtDate(r.effective_from) > today);
    }
    return list;
  }, [rows, futureOnly]);

  const enriched = useMemo(() => pairWithPrior(filtered), [filtered]);
  const grouped = useMemo(() => groupByMonth(enriched), [enriched]);

  // Calendar grid for the current cursor month
  const grid = useMemo(() => {
    const [yy, mm] = cursor.split("-").map((s) => parseInt(s, 10));
    const first = new Date(Date.UTC(yy, mm - 1, 1));
    const last = new Date(Date.UTC(yy, mm, 0));
    const daysInMonth = last.getUTCDate();
    const startDow = first.getUTCDay();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(`${cursor}-${String(d).padStart(2, "0")}`);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [cursor]);

  const eventsByDay = useMemo(() => {
    const map = {};
    for (const r of enriched) {
      const d = fmtDate(r.effective_from);
      if (!map[d]) map[d] = [];
      map[d].push(r);
    }
    return map;
  }, [enriched]);

  const cursorLabel = useMemo(() => {
    const [yy, mm] = cursor.split("-").map((s) => parseInt(s, 10));
    return new Date(Date.UTC(yy, mm - 1, 1)).toLocaleString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
  }, [cursor]);

  const shiftMonth = (delta) => {
    const [yy, mm] = cursor.split("-").map((s) => parseInt(s, 10));
    const dt = new Date(Date.UTC(yy, mm - 1 + delta, 1));
    setCursor(`${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}`);
    setSelectedDay(null);
  };

  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <div data-testid="rate-calendar-page">
      <PageHeader
        title="Rate Calendar"
        subtitle="All rate versions across merchants, partners, and gateways. Use this view to plan renegotiations and audit historical changes."
      />
      <div className="p-4 sm:p-10 space-y-4">
        <div className="flex items-center gap-2 flex-wrap" data-testid="rc-filter-bar">
          <div className="inline-flex border border-[var(--line)] rounded-sm overflow-hidden">
            <button
              type="button" data-testid="rc-view-timeline"
              onClick={() => setView("timeline")}
              className={`px-3 h-9 text-[12.5px] font-medium ${view === "timeline" ? "bg-[var(--ink)] text-white" : "bg-[var(--surface)] text-[var(--ink)] hover:bg-[var(--surface-2)]"}`}
            ><History size={12} className="inline mr-1.5 -mt-0.5" /> Timeline</button>
            <button
              type="button" data-testid="rc-view-calendar"
              onClick={() => setView("calendar")}
              className={`px-3 h-9 text-[12.5px] font-medium border-l border-[var(--line)] ${view === "calendar" ? "bg-[var(--ink)] text-white" : "bg-[var(--surface)] text-[var(--ink)] hover:bg-[var(--surface-2)]"}`}
            ><Calendar size={12} className="inline mr-1.5 -mt-0.5" /> Calendar</button>
          </div>
          <div className="w-44">
            <Select
              testid="rc-scope-filter"
              placeholder="All scopes"
              options={[
                { value: "merchant_mid", label: "Merchant" },
                { value: "gateway_lob", label: "Gateway · LOB" },
                { value: "partner_lob", label: "Partner · LOB" },
              ]}
              value={scopeFilter}
              onChange={setScopeFilter}
            />
          </div>
          <label className="inline-flex items-center gap-2 text-[12.5px] text-[var(--ink)] cursor-pointer">
            <input type="checkbox" checked={futureOnly} onChange={(e) => setFutureOnly(e.target.checked)} data-testid="rc-future-only" />
            Future only
          </label>
          <Btn variant="ghost" testid="rc-refresh" onClick={load}>Refresh</Btn>
        </div>

        {view === "timeline" && (
          <Section title={`${enriched.length} rate version${enriched.length === 1 ? "" : "s"}${futureOnly ? " · upcoming" : ""}`} testid="rc-timeline-section">
            {loading && <div className="p-6 text-center text-[var(--muted)] text-[12px]">Loading…</div>}
            {!loading && grouped.size === 0 && (
              <div className="p-6 text-center text-[var(--muted)] text-[12px]">No versions match.</div>
            )}
            {!loading && [...grouped.entries()].map(([month, items]) => {
              const dt = new Date(`${month}-01T00:00:00Z`);
              const monthLabel = dt.toLocaleString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
              const isFuture = month > todayIso.slice(0, 7);
              return (
                <div key={month} className="border-t border-[var(--line)] first:border-t-0">
                  <div className={`px-4 py-2 text-[10.5px] uppercase tracking-[0.2em] font-semibold ${isFuture ? "bg-amber-50 text-amber-900" : "bg-[var(--surface-2)] text-[var(--muted)]"}`} data-testid={`rc-month-${month}`}>
                    {monthLabel}{isFuture ? " · upcoming" : ""}
                  </div>
                  <table className="table-data w-full">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Scope</th>
                        <th>Entity</th>
                        <th className="text-right">Old</th>
                        <th className="text-right">New</th>
                        <th>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((r) => (
                        <tr key={r.id} data-testid={`rc-row-${r.id}`}>
                          <td className="num text-[12px]">{fmtDate(r.effective_from)}</td>
                          <td className="text-[11.5px] uppercase tracking-wider text-[var(--muted)]">{SCOPE_LABEL[r.scope] || r.scope}</td>
                          <td className="text-[12px] font-medium">{describeKey(r, merchantsById)}</td>
                          <td className="num text-right text-[12px] text-[var(--muted)]">{r._prior_rate != null ? fmtRate(r._prior_rate) : "—"}</td>
                          <td className="num text-right text-[12px] font-semibold text-[var(--ink)]">{fmtRate(r.rate_value)}</td>
                          <td className="text-[11px] text-[var(--muted)] max-w-[260px] truncate" title={r.reason || ""}>{r.reason || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </Section>
        )}

        {view === "calendar" && (
          <Section
            title={`Calendar · ${cursorLabel}`}
            testid="rc-calendar-section"
            right={
              <div className="flex items-center gap-1">
                <button data-testid="rc-prev-month" onClick={() => shiftMonth(-1)} className="p-1.5 rounded-sm hover:bg-[var(--surface-2)]"><ChevronLeft size={14} /></button>
                <button data-testid="rc-next-month" onClick={() => shiftMonth(1)} className="p-1.5 rounded-sm hover:bg-[var(--surface-2)]"><ChevronRight size={14} /></button>
              </div>
            }
          >
            <div className="grid grid-cols-7 gap-px bg-[var(--line)] p-px" data-testid="rc-calendar-grid">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                <div key={d} className="bg-[var(--surface-2)] py-2 text-center text-[10px] uppercase tracking-wider font-semibold text-[var(--muted)]">{d}</div>
              ))}
              {grid.map((day, i) => {
                if (!day) return <div key={i} className="bg-[var(--surface-2)] min-h-[80px]" />;
                const events = eventsByDay[day] || [];
                const isToday = day === todayIso;
                const isSelected = day === selectedDay;
                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => setSelectedDay(events.length ? day : null)}
                    data-testid={`rc-day-${day}`}
                    className={`bg-[var(--surface)] min-h-[80px] p-1.5 text-left transition-colors ${
                      events.length ? "hover:bg-[var(--surface-2)] cursor-pointer" : "cursor-default"
                    } ${isSelected ? "ring-1 ring-[var(--accent)]" : ""}`}
                  >
                    <div className={`text-[11px] font-semibold mb-1 ${isToday ? "text-[var(--accent)]" : "text-[var(--ink)]"}`}>
                      {parseInt(day.slice(-2), 10)}
                    </div>
                    {events.slice(0, 3).map((ev) => (
                      <div key={ev.id} className="text-[10px] truncate text-[var(--ink-2)] bg-amber-100/60 px-1.5 py-0.5 rounded-sm mb-0.5" title={`${describeKey(ev, merchantsById)} → ${fmtRate(ev.rate_value)}`}>
                        {describeKey(ev, merchantsById)}
                      </div>
                    ))}
                    {events.length > 3 && <div className="text-[9.5px] text-[var(--muted)]">+{events.length - 3} more</div>}
                  </button>
                );
              })}
            </div>

            {selectedDay && (
              <div className="border-t border-[var(--line)]" data-testid="rc-day-detail">
                <div className="px-4 py-3 bg-[var(--surface-2)] text-[11px] uppercase tracking-wider font-semibold text-[var(--muted)]">
                  Events on {selectedDay}
                </div>
                <table className="table-data w-full">
                  <thead><tr><th>Scope</th><th>Entity</th><th className="text-right">Old</th><th className="text-right">New</th><th>Reason</th></tr></thead>
                  <tbody>
                    {(eventsByDay[selectedDay] || []).map((r) => (
                      <tr key={r.id}>
                        <td className="text-[11.5px] uppercase tracking-wider text-[var(--muted)]">{SCOPE_LABEL[r.scope] || r.scope}</td>
                        <td className="text-[12px] font-medium">{describeKey(r, merchantsById)}</td>
                        <td className="num text-right text-[12px] text-[var(--muted)]">{r._prior_rate != null ? fmtRate(r._prior_rate) : "—"}</td>
                        <td className="num text-right text-[12px] font-semibold">{fmtRate(r.rate_value)}</td>
                        <td className="text-[11px] text-[var(--muted)]">{r.reason || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        )}
      </div>
    </div>
  );
}
